import { Link, NavLink } from "react-router-dom";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Menu, Globe, ChevronRight } from "lucide-react";
import ThomLogo from "@/components/ThomLogo";
import NotificationBell from "@/components/NotificationBell";
import {
  Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";

/**
 * Continental (parent-brand) navigation for the /africa/* routes.
 * Distinct from the operational country navbar to reinforce that
 * THOM Verify Africa is the corporate parent, not a marketplace.
 */
const LINKS = [
  { to: "/africa/about",               label: "About",                testid: "africa-nav-about" },
  { to: "/africa/services",            label: "Services",             testid: "africa-nav-services" },
  { to: "/africa/industries",          label: "Industries",           testid: "africa-nav-industries" },
  { to: "/africa/countries",           label: "Countries",            testid: "africa-nav-countries" },
  { to: "/admin/command-centre",       label: "Command Centre",       testid: "africa-nav-command-centre" },
  { to: "/partners/onboard",           label: "Partner Onboarding",   testid: "africa-nav-onboard" },
  { to: "/africa/enterprise",          label: "Enterprise",           testid: "africa-nav-enterprise" },
  { to: "/africa/trust-infrastructure",label: "Trust Infrastructure", testid: "africa-nav-trust" },
  { to: "/trust-centre",               label: "Trust Centre",         testid: "africa-nav-trust-centre" },
  { to: "/track-verification",         label: "Track a Request",      testid: "africa-nav-track" },
  { to: "/network",                    label: "Trust Network",        testid: "africa-nav-network" },
  { to: "/verify",                     label: "Verify Certificate",   testid: "africa-nav-verify" },
];

export default function AfricaNavbar() {
  const [open, setOpen] = useState(false);
  return (
    <header className="sticky top-0 z-40 bg-[#0A0A0A] text-white border-b border-white/10">
      <div className="max-w-[1500px] mx-auto flex items-center justify-between px-4 md:px-6 h-20 gap-3">
        <Link to="/africa" data-testid="africa-brand-home" className="shrink-0 focus:outline-none">
          <div className="flex items-center gap-3">
            <ThomLogo size={40} variant="onDark" />
            <div className="hidden md:flex flex-col leading-tight">
              <span className="text-[10px] mono uppercase tracking-[0.22em] text-white/50">Africa · Parent Platform</span>
              <span className="text-[13px] tracking-wide font-medium">THOM Verify™ Africa</span>
            </div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-6 text-[12.5px] tracking-wide">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.testid}
              className={({ isActive }) => `text-white/75 hover:text-white transition-colors ${isActive ? "text-white" : ""}`}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <NotificationBell variant="dark" />
          <Link to="/africa/countries" className="hidden sm:inline-flex">
            <Button size="sm" variant="outline" className="border-white/25 text-white hover:bg-white/10 hover:text-white" data-testid="africa-nav-select-country">
              <Globe className="h-3.5 w-3.5 mr-1.5" /> Select Country
            </Button>
          </Link>
          <Link to="/africa/enterprise#request" className="hidden md:inline-flex">
            <Button size="sm" className="bg-[#B08C4A] hover:bg-[#8f6f34] text-black" data-testid="africa-nav-request">
              Request Verification <ChevronRight className="h-3.5 w-3.5 ml-1" />
            </Button>
          </Link>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="lg:hidden text-white hover:bg-white/10" aria-label="Open menu" data-testid="africa-nav-mobile-trigger">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] sm:w-[360px] bg-[#0A0A0A] text-white border-white/10" data-testid="africa-nav-mobile-panel">
              <SheetHeader><SheetTitle className="text-left text-white">THOM Verify™ Africa</SheetTitle></SheetHeader>
              <nav className="mt-6 flex flex-col divide-y divide-white/10">
                {LINKS.map((l) => (
                  <NavLink
                    key={l.to}
                    to={l.to}
                    onClick={() => setOpen(false)}
                    data-testid={`${l.testid}-mobile`}
                    className={({ isActive }) => `py-3 text-sm ${isActive ? "text-white font-semibold" : "text-white/80"}`}
                  >
                    {l.label}
                  </NavLink>
                ))}
                <div className="pt-4 flex flex-col gap-2">
                  <Link to="/africa/countries" onClick={() => setOpen(false)}>
                    <Button className="w-full bg-[#B08C4A] hover:bg-[#8f6f34] text-black">Select Country</Button>
                  </Link>
                  <Link to="/africa/enterprise" onClick={() => setOpen(false)}>
                    <Button variant="outline" className="w-full border-white/25 text-white hover:bg-white/10 hover:text-white">Request Verification</Button>
                  </Link>
                </div>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
